import type { Ruolo } from '../api/tipi-dati.ts';

/**
 * Chi può fare cosa, per l'interfaccia.
 *
 * ⚠️ **È il gemello di `backend/src/lib/ruoli.ts`, e la copia è voluta**: i due
 * lati si compilano separatamente e non condividono codice, come tutto il resto
 * del contratto. Se là cambia la tabella dei poteri, va cambiata anche qui —
 * nessun compilatore se ne accorgerà.
 *
 * 🔴 **E questo file non decide niente sul serio.** Le vere guardie stanno sul
 * server: qui si decide solo cosa *mostrare*. Nascondere un pulsante non
 * impedisce a nessuno di chiamare la rotta a mano, e non è mai stato l'intento.
 */

/** In ordine di gerarchia, dal più al meno: l'elenco si legge come una scala. */
export const ORDINE_RUOLI: readonly Ruolo[] = ['AMMINISTRATORE', 'REDATTORE', 'OPERATORE'];

const POTERI: Record<Ruolo, { amministra: boolean; redige: boolean }> = {
  AMMINISTRATORE: { amministra: true, redige: true },
  REDATTORE: { amministra: false, redige: true },
  OPERATORE: { amministra: false, redige: false },
};

/*
 * ⚠️ **Accettano `undefined`, al contrario dei gemelli del backend.**
 *
 * Tutti i punti dell'interfaccia leggono `utente?.ruolo`, e nel primo istante —
 * mentre la sessione si sta ancora verificando — l'utente non c'è. Se
 * `undefined` finisse in un confronto invertito (`!== 'AMMINISTRATORE'`),
 * l'ingranaggio delle impostazioni comparirebbe per un fotogramma a chi non
 * deve vederlo. Qui `undefined` vuol dire «non ancora, e quindi no».
 */

/** Configura l'installazione: impostazioni, utenti, registro di tutto l'ufficio. */
export function puoAmministrare(ruolo: Ruolo | undefined): boolean {
  return ruolo !== undefined && POTERI[ruolo].amministra;
}

/** Scrive sui dati dell'ufficio: catalogo, anagrafiche, cancellazioni definitive. */
export function puoRedigere(ruolo: Ruolo | undefined): boolean {
  return ruolo !== undefined && POTERI[ruolo].redige;
}

export const ETICHETTE_RUOLO: Record<Ruolo, string> = {
  AMMINISTRATORE: 'Amministratore',
  REDATTORE: 'Redattore',
  OPERATORE: 'Operatore',
};

export const SPIEGAZIONE_RUOLO: Record<Ruolo, string> = {
  AMMINISTRATORE:
    'Oltre al lavoro sulle scadenze gestisci anche gli utenti, le impostazioni, il server di posta e il registro di tutto l’ufficio.',
  REDATTORE:
    'Fai tutto il lavoro sui dati: scadenze, soggetti, tipi e categorie, comprese la disattivazione e la cancellazione definitiva. Impostazioni e utenti restano agli amministratori, e del registro vedi le tue attività.',
  OPERATORE:
    'Lavori sulle scadenze, sui soggetti e sugli allegati. Catalogo, utenti e impostazioni li gestiscono amministratori e redattori.',
};

/**
 * Il valore arrivato da un menu a tendina è davvero un ruolo?
 *
 * Sostituisce un `as Ruolo` che non verificava niente: un `as` è una promessa
 * fatta al compilatore, e le promesse sul contenuto di un `<select>` è meglio
 * mantenerle a runtime.
 */
export function eRuolo(valore: string): valore is Ruolo {
  return (ORDINE_RUOLI as readonly string[]).includes(valore);
}
